/**
 * Dots & Boxes Board Component
 *
 * Renders a grid of dots with clickable lines between them.
 * Completed boxes are filled with the owner's color.
 * Hover preview on valid (undrawn) lines.
 */

import React, { useCallback, useState } from 'react';
import {
  DotsBoxesBoardProps,
  Line,
  PlayerColor,
} from '../types';
import { DotsBoxesEngine } from '../DotsBoxesEngine';
import './DotsBoxesBoard.css';

const PLAYER_COLOR_MAP: Record<PlayerColor, string> = {
  blue: '#2196F3',
  red: '#F44336',
  green: '#4CAF50',
  orange: '#FF9800',
};

const PLAYER_COLOR_LIGHT_MAP: Record<PlayerColor, string> = {
  blue: 'rgba(33, 150, 243, 0.2)',
  red: 'rgba(244, 67, 54, 0.2)',
  green: 'rgba(76, 175, 80, 0.2)',
  orange: 'rgba(255, 152, 0, 0.2)',
};

const DotsBoxesBoard: React.FC<DotsBoxesBoardProps> = ({
  board,
  gridSize,
  currentPlayer,
  onLineClick,
  disabled = false,
}) => {
  const [hoveredLine, setHoveredLine] = useState<string | null>(null);

  const getLineKey = useCallback((line: Line): string => {
    return `${line.row},${line.col},${line.direction}`;
  }, []);

  const handleLineClick = useCallback(
    (line: Line) => {
      if (disabled) return;
      const isDrawn =
        line.direction === 'horizontal'
          ? board.horizontalLines[line.row]?.[line.col]
          : board.verticalLines[line.row]?.[line.col];
      if (isDrawn) return;
      onLineClick(line);
    },
    [disabled, board, onLineClick]
  );

  const handleLineHover = useCallback(
    (line: Line | null) => {
      if (disabled || !line) {
        setHoveredLine(null);
        return;
      }
      const isDrawn =
        line.direction === 'horizontal'
          ? board.horizontalLines[line.row]?.[line.col]
          : board.verticalLines[line.row]?.[line.col];
      if (isDrawn) {
        setHoveredLine(null);
        return;
      }
      setHoveredLine(getLineKey(line));
    },
    [disabled, board, getLineKey]
  );

  const renderDot = (row: number, col: number) => (
    <div
      key={`dot-${row}-${col}`}
      className="dots-board__dot"
      aria-hidden="true"
    />
  );

  const renderHorizontalLine = (row: number, col: number) => {
    const line: Line = { row, col, direction: 'horizontal' };
    const isDrawn = board.horizontalLines[row]?.[col] ?? false;
    const lineKey = getLineKey(line);
    const isHovered = hoveredLine === lineKey;
    const owner = board.lineOwners[lineKey];

    const lineColor = isDrawn && owner ? PLAYER_COLOR_MAP[owner] : undefined;
    const hoverColor = isHovered ? PLAYER_COLOR_MAP[currentPlayer] : undefined;

    const classes = [
      'dots-board__line',
      'dots-board__line--horizontal',
      isDrawn ? 'dots-board__line--drawn' : 'dots-board__line--undrawn',
      isHovered && !isDrawn ? 'dots-board__line--hovered' : '',
      disabled ? 'dots-board__line--disabled' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <button
        key={`h-${row}-${col}`}
        className={classes}
        style={{
          backgroundColor: lineColor || hoverColor || undefined,
        }}
        onClick={() => handleLineClick(line)}
        onMouseEnter={() => handleLineHover(line)}
        onMouseLeave={() => setHoveredLine(null)}
        disabled={disabled || isDrawn}
        aria-label={`Horizontal line between dot ${row},${col} and dot ${row},${col + 1}${isDrawn ? ' (drawn)' : ''}`}
        tabIndex={!disabled && !isDrawn ? 0 : -1}
      />
    );
  };

  const renderVerticalLine = (row: number, col: number) => {
    const line: Line = { row, col, direction: 'vertical' };
    const isDrawn = board.verticalLines[row]?.[col] ?? false;
    const lineKey = getLineKey(line);
    const isHovered = hoveredLine === lineKey;
    const owner = board.lineOwners[lineKey];

    const lineColor = isDrawn && owner ? PLAYER_COLOR_MAP[owner] : undefined;
    const hoverColor = isHovered ? PLAYER_COLOR_MAP[currentPlayer] : undefined;

    const classes = [
      'dots-board__line',
      'dots-board__line--vertical',
      isDrawn ? 'dots-board__line--drawn' : 'dots-board__line--undrawn',
      isHovered && !isDrawn ? 'dots-board__line--hovered' : '',
      disabled ? 'dots-board__line--disabled' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <button
        key={`v-${row}-${col}`}
        className={classes}
        style={{
          backgroundColor: lineColor || hoverColor || undefined,
        }}
        onClick={() => handleLineClick(line)}
        onMouseEnter={() => handleLineHover(line)}
        onMouseLeave={() => setHoveredLine(null)}
        disabled={disabled || isDrawn}
        aria-label={`Vertical line between dot ${row},${col} and dot ${row + 1},${col}${isDrawn ? ' (drawn)' : ''}`}
        tabIndex={!disabled && !isDrawn ? 0 : -1}
      />
    );
  };

  const renderBox = (row: number, col: number) => {
    const owner = board.boxes[row]?.[col];
    const sides = DotsBoxesEngine.countBoxSides(board, row, col, gridSize);

    const classes = [
      'dots-board__box',
      owner ? `dots-board__box--claimed` : '',
      owner ? `dots-board__box--${owner}` : '',
    ]
      .filter(Boolean)
      .join(' ');

    const bgColor = owner ? PLAYER_COLOR_LIGHT_MAP[owner] : undefined;

    return (
      <div
        key={`box-${row}-${col}`}
        className={classes}
        style={{ backgroundColor: bgColor }}
        aria-label={owner ? `Box claimed by ${owner}` : `Unclaimed box with ${sides} sides`}
      >
        {owner && (
          <span
            className="dots-board__box-initial"
            style={{ color: PLAYER_COLOR_MAP[owner] }}
            aria-hidden="true"
          >
            {owner.charAt(0).toUpperCase()}
          </span>
        )}
      </div>
    );
  };

  // Build the grid row by row
  // Pattern: dot-row, then line-row, alternating
  const rows: React.ReactNode[] = [];

  for (let row = 0; row < gridSize; row++) {
    // Dot row: dot, h-line, dot, h-line, ..., dot
    const dotRow: React.ReactNode[] = [];
    for (let col = 0; col < gridSize; col++) {
      dotRow.push(renderDot(row, col));
      if (col < gridSize - 1) {
        dotRow.push(renderHorizontalLine(row, col));
      }
    }
    rows.push(
      <div key={`dot-row-${row}`} className="dots-board__dot-row">
        {dotRow}
      </div>
    );

    // Line row (between dot rows): v-line, box, v-line, box, ..., v-line
    if (row < gridSize - 1) {
      const lineRow: React.ReactNode[] = [];
      for (let col = 0; col < gridSize; col++) {
        lineRow.push(renderVerticalLine(row, col));
        if (col < gridSize - 1) {
          lineRow.push(renderBox(row, col));
        }
      }
      rows.push(
        <div key={`line-row-${row}`} className="dots-board__line-row">
          {lineRow}
        </div>
      );
    }
  }

  return (
    <div
      className="dots-board"
      role="grid"
      aria-label="Dots and Boxes game board"
    >
      {rows}
    </div>
  );
};

export { DotsBoxesBoard };
export default DotsBoxesBoard;
